import {
  Box,
  CircularProgress,
} from '@mui/material';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import BaseHighchart from '../../BaseHighchart';
import {
  ChartDataType,
} from '../../types';
import { calculateSeries } from '../builder';

import { HUMIDITY_TEMPERATURE_NAME, CHART_TITLE_HUMEDAD_TEMPERATURA } from '../../../../constants/graphs';
import apiClient from '../../../../requests/api/apiClient';
import { ConversationSchema, TimePointSchema } from '../../../../requests/api/apiTypes';
import { ReduxState } from '../../../../types';
import { navigateToChart } from '../../../../utils/helpers';
import { FetchError } from '../../../../views/types';
import ChartFilter from '../../ChartFilter';
import ConversationModal from '../../ConversationTab/conversationModal';
import { FormattedSeries } from '../types';
import {
  getChartConfig,
  getTemperatureHumidityTooltip,
  getYAxis,
  calculateYAxisLimits,
} from '../utils';
import { modifySeriesData } from './utils';
import ChartConfigDialog from '../../ChartConfigDialog/ChartConfigDialog';
import HumidityTemperatureConfigContent from './HumidityTemperatureConfigContent';
import useChartConfigClick from '../../../../hooks/useChartConfigClick';

function HumidityTemperatureChart({
  chartView,
  sectorId,
  farmId,
  hideLegend = false,
  hideXValues = false,
  hideYValues = false,
  hideRightYAxisTitle = false,
  height = 600,
  title = CHART_TITLE_HUMEDAD_TEMPERATURA,
  yAxisTitle = 'Temperatura (ºC)',
  externalModalState,
  externalSetModalState,
  externalConversations,
  externalSetConversations,
  onConversationCreated,
  isWeatherStation,
}: {
  chartView: 'dashboard' | 'graphs' | 'settings' | 'fullScreen'
  sectorId: string
  farmId: string
  hideLegend?: boolean
  hideXValues?: boolean
  hideYValues?: boolean
  hideRightYAxisTitle?: boolean
  height?: number | string
  title?: string
  yAxisTitle?: string
  externalModalState?: {
    open: boolean;
    conversationId: string;
    timePointData: TimePointSchema;
  };
  externalSetModalState?: React.Dispatch<React.SetStateAction<{
    open: boolean;
    conversationId: string;
    timePointData: TimePointSchema;
  }>>;
  externalConversations?: ConversationSchema[];
  externalSetConversations?: React.Dispatch<React.SetStateAction<ConversationSchema[]>>;
  onConversationCreated?: () => void;
  isWeatherStation?: boolean;
}) {
  const chartName = 'temperatura-humedad';
  const [chartData, setChartData] = useState<ChartDataType>({
    series: [],
    categories: [],
    absoluteCategories: [],
  });

  const [isLoading, setIsLoading] = useState(true);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const [internalModalState, setInternalModalState] = useState<{
    open: boolean;
    conversationId: string;
    timePointData: TimePointSchema;
  }>({
    open: false,
    conversationId: '',
    timePointData: {
      hour: '',
      day: '',
      month: '',
      year: '',
    },
  });
  const [internalConversations, setInternalConversations] = useState<ConversationSchema[]>([]);

  const { interval } = useSelector((state: ReduxState) => state.graphsFilter);
  const selectedSector = useSelector((state: ReduxState) => state.currentSector.selectedSector);

  const modalState = externalModalState || internalModalState;
  const setModalState = externalSetModalState || setInternalModalState;
  const conversations = externalConversations || internalConversations;
  const setConversations = externalSetConversations || setInternalConversations;

  const transformData = useCallback(
    (
      preparedData: FormattedSeries[],
    ) => {
      const seriesData = calculateSeries(
        preparedData,
        true,
        interval?.startDate || '',
        interval?.endDate || '',
      );

      const modifiedSeries = modifySeriesData(seriesData.series);

      setChartData({
        ...seriesData,
        series: modifiedSeries,
      });
    }, [interval?.startDate, interval?.endDate],
  );

  const fetchData = useCallback(async () => {
    setIsLoading(true);

    try {
      if (interval) {
        const { data } = await apiClient.readings.getHumidityTemperatureChartData({
          farmId,
          sectorId,
          startDate: interval?.startDate || '',
          endDate: interval?.endDate || '',
          telemetryType: selectedSector?.telemetryType,
          sn: selectedSector?.telemetryType === 'sn' ? selectedSector.weatherZoneId : undefined,
          chart: HUMIDITY_TEMPERATURE_NAME,
          isWeatherStation,
        });

        transformData(data.series);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [farmId,
    interval,
    transformData,
    selectedSector,
    sectorId,
    isWeatherStation,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const customTooltip = useMemo(() => ({
    shared: true,
    useHTML: true,
    formatter(this: Highcharts.TooltipFormatterContextObject) {
      return getTemperatureHumidityTooltip(this, conversations);
    },
  }), [conversations]);

  const chartConfig = useMemo(() => {
    if (!interval) return null;

    const temperatureSeries = chartData.series.filter((series: any) => series.yAxis === 0 || series.name?.includes('Temperature'));
    const humiditySeries = chartData.series.filter((series: any) => series.name?.includes('Humidity'));
    const temperatureLimits = calculateYAxisLimits(temperatureSeries);
    const humidityLimits = calculateYAxisLimits(humiditySeries);

    const yAxisConfig: Highcharts.YAxisOptions[] = [
      {
        ...getYAxis(yAxisTitle || null, hideYValues),
        id: 'temperature',
        min: temperatureLimits.min,
        max: temperatureLimits.max,
      } as Highcharts.YAxisOptions,
      {
        ...getYAxis(hideRightYAxisTitle || !yAxisTitle ? null : 'Humedad (%)', hideYValues || hideRightYAxisTitle),
        id: 'humidity',
        opposite: true,
        min: humidityLimits.min,
        max: humidityLimits.max,
      } as Highcharts.YAxisOptions,
    ];

    return ({
      ...getChartConfig(
        chartData,
        height,
        hideXValues,
        undefined,
        dayjs(interval.startDate).valueOf(),
        dayjs(interval.endDate).valueOf(),
      ),
      yAxis: yAxisConfig,
      plotOptions: {
        series: {
          marker: {
            enabled: true,
          },
        },
      },
    });
  }, [
    chartData,
    height,
    hideYValues,
    hideRightYAxisTitle,
    interval?.startDate,
    interval?.endDate,
  ]);

  const filteredSeries = useMemo(() => chartData.series, [chartData.series]);

  useEffect(() => {
    if (!externalConversations) {
      const loadConversations = async () => {
        try {
          const { data } = await apiClient.conversation.conversationDetail(
            sectorId,
            HUMIDITY_TEMPERATURE_NAME,
          );
          setInternalConversations(data.conversations);
        } catch (err) {
          const { error } = err as FetchError;
          enqueueSnackbar(error.message, { variant: 'error' });
        }
      };

      loadConversations();
    }
  }, [sectorId, externalConversations]);

  const handleConfigClick = useChartConfigClick({
    chartView,
    route: chartName,
    farmId,
    sectorId,
    onFullScreen: () => setConfigDialogOpen(true),
  });

  const handleFullScreenClick = useCallback(() => {
    navigateToChart(`fullScreen/${chartName}`, history, {
      farmId,
      sectorId,
    });
  }, [history, farmId, sectorId]);

  return (
    <div>
      {isLoading && (
        <Box display="flex" justifyContent="center" alignItems="center" height={height}>
          <CircularProgress />
        </Box>
      )}
      <div style={{ visibility: isLoading ? 'hidden' : 'visible' }}>
      <BaseHighchart
        chartView={chartView}
        sectorId={sectorId}
        filter={chartView === 'settings' ? <ChartFilter sectorId={sectorId} /> : undefined}
        title={title}
        yAxisTitle={yAxisTitle}
        series={filteredSeries as Highcharts.SeriesOptionsType[]}
        tooltip={customTooltip}
        hideYValues={hideYValues}
        adjustYWithLegend={true}
        showFullScreen={true}
        legend={{ enabled: !hideLegend }}
        chartConfig={chartConfig}
        onConfigClick={handleConfigClick}
        onFullScreenClick={handleFullScreenClick}
        absoluteCategories={chartData.absoluteCategories}
        setModalState={setModalState}
        conversations={conversations}
        height={height}
        zoomSync={true}
        autoAdjustYAxis={true}
        interval={interval}
        graphName={HUMIDITY_TEMPERATURE_NAME}
      />
      </div>
      <ConversationModal
        modalState={modalState}
        setModalState={setModalState}
        farmId={farmId}
        sectorId={sectorId}
        chart={HUMIDITY_TEMPERATURE_NAME}
        setConversationsFunction={setConversations}
        onConversationCreated={onConversationCreated}
      />
      <ChartConfigDialog
        open={configDialogOpen}
        onClose={() => setConfigDialogOpen(false)}
        title="Configuración · Humedad y Temperatura"
      >
        <HumidityTemperatureConfigContent sectorId={sectorId} />
      </ChartConfigDialog>
    </div>
  );
}

export default HumidityTemperatureChart;
